import { useCallback, useEffect, useState } from 'react';

import { PageTitle } from '@components/PageTitle';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import { Label } from '@components/ui/label';
import { useAuth } from '@contexts/auth';
import { useScroll } from '@contexts/scroll';
import errorsValidation from '@errors/validation';
import formatterDecimal from '@formatters/decimal';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@lib/ui/cn';
import { type CalculatorPediatrics, type Topics } from '@models/Calculator';
import { ChevronLeft, ChevronRightIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { NumberFormatBase, NumericFormat } from 'react-number-format';
import { Navigate } from 'react-router';
import { z } from 'zod';

import { useCalculator } from '../../context';
import Content from '../content';

const pediatricEmergencyDose: CalculatorPediatrics = {
  name: 'Dose Emergência Pediatria',
  sections: [
    {
      title: 'Parada Cardiorrespiratória',
      topics: [
        {
          title: 'EPINEFRINA',
          content: {
            applicationUnitMeasure: 'mL IV bolus',
            diluteDefault: 1,
            diluteUnitMeasureDefault: '1',
            doseFixed: 2,
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
              {
                label: 'mL',
                value: '2',
              },
            ],
            doseDefault: 0.01,
            doseTwoDefault: 0.1,
            drugDefault: JSON.stringify({
              mg: 1,
              ml: 1,
            }),
            drugFieldWidth: 0.35,
            drugOptions: [
              {
                label: '1mg/1mL',
                value: JSON.stringify({
                  mg: 1,
                  ml: 1,
                }),
              },
            ],
            multiplyDoseMax: 1,
            multiplyDoseTwoMax: 2.5,
            observations: [
              'Intravenoso (IV): Após cada dose, aplicar flush de 10mL de SF 0,9% e elevar membro',
            ],
            salineDefault: 9,
            salineLabel: 'SF 0,9%',
            showDoseTwo: true,
            suggestions: [
              'Intravenoso (IV): Diluir 1 amp + 9mL SF 0,9% e infundir uma dose de 0,01mg/Kg (máx: 1mg)',
              'Intraósseo (IO): Dose de 0,01mg/Kg (máx: 1mg)',
              'Endotraqueal: Dose de 0,1mg/Kg (máx 2,5mg) (não diluir)',
            ],
            templateOne: true,
            title: 'Epinefrina',
          },
        },
        {
          title: 'AMIODARONA',
          content: {
            applicationUnitMeasure: 'mL IV bolus',
            diluteDefault: 1,
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
              {
                label: 'mL',
                value: '2',
              },
            ],
            doseDefault: 5,
            drugDefault: JSON.stringify({
              mg: 150,
              ml: 3,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '150mg/3mL',
                value: JSON.stringify({
                  mg: 150,
                  ml: 3,
                }),
              },
            ],
            multiplyDoseMax: 150,
            observations: ['Máx de até 3x'],
            salineDefault: 0,
            salineLabel: 'SG 5%',
            suggestions: ['Não diluir', '5mg/Kg'],
            templateOne: true,
            title: 'Amiodarona',
          },
        },
        {
          title: 'LIDOCAÍNA',
          content: {
            applicationUnitMeasure: 'mL IV bolus',
            diluteDefault: 1,
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
              {
                label: 'mL',
                value: '2',
              },
            ],
            doseDefault: 1,
            drugDefault: JSON.stringify({
              mg: 10,
              ml: 1,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '10mg/1mL',
                value: JSON.stringify({
                  mg: 10,
                  ml: 1,
                }),
              },
              {
                label: '20mg/1mL',
                value: JSON.stringify({
                  mg: 20,
                  ml: 1,
                }),
              },
            ],
            salineDefault: 0,
            salineLabel: 'SF 0,9%',
            suggestions: ['Não diluir', '1mg/Kg'],
            templateOne: true,
            title: 'Lidocaína',
          },
        },
        {
          title: 'DESFIBRILAÇÃO',
          content: {
            chargeDefault: 2,
            chargeTwoDefault: 4,
            description: 'Afastar equipe e fluxo de O2',
            templateTwo: true,
            title: 'Desfibrilação',
          },
        },
      ],
    },
    {
      title: 'Broncodilatadores',
      topics: [
        {
          title: 'FENOTEROL NEB.',
          content: {
            applicationUnitMeasure:
              'gotas + 3 a 4mL SF 0,9%, de 20/20min na 1ª hora',
            doseFixed: 2,
            nebMax: 20,
            doseDefault: 0.05,
            drugDefault: JSON.stringify({
              mg: 5,
              ml: 1,
              drops: 20,
            }),
            drugOptions: [
              {
                label: '5mg/1mL/20 gotas',
                value: JSON.stringify({
                  mg: 5,
                  ml: 1,
                  drops: 20,
                }),
              },
            ],
            observations: [
              'Diluir em 3-4 mL de SF0,9% estéril com fluxo de O2 de 6 a 8L/min (preferir nebulizar com fluxo de O2, ao invés de ar comprimido).',
            ],
            suggestions: [
              'Nebulização com 0,05 mg/kg/dos, a cada 20 min na primeira hora. Avaliar a necessidade de doses adicionais conforme a resposta do paciente.',
            ],
            templateThree: true,
            title: 'Fenoterol',
          },
        },
        {
          title: 'FENOTEROL SPRAY',
          content: {
            applicationUnitMeasure: 'de 20/20min na 1ª hora',
            drugDefault: JSON.stringify({
              mg: 100,
              ml: 1,
            }),
            drugFieldWidth: 0.46,
            drugOptions: [
              {
                label: '100mcg/1 puff',
                value: JSON.stringify({
                  mg: 100,
                  ml: 1,
                }),
              },
            ],
            observations: [
              'Se os sintomas persistirem ou retornarem, administrar doses adicionais de 2 a 3 puffs por hora.',
              'Se os sintomas melhorarem e o paciente receber alta, manter uso sob demanda (2 puffs a cada 4 a horas, se necessário',
            ],
            suggestions: [
              'Aplicar 2 (leves) e 6 (graves) puffs a cada 20 minutos durante a 1ª hora, depois reavaliar a gravidade (na prática normalmente se considera 1 puff para cada 3Kg, sendo no máximo 10 puffs)',
            ],
            templateFour: true,
            templateFourCalc2: true,
            title: 'Fenoterol',
          },
        },
        {
          title: 'SALBUTAMOL NEB.',
          content: {
            applicationUnitMeasure:
              'mL + 3 a 4mL SF 0,9%, de 20/20min na 1ª hora',
            doseDefault: 0.15,
            doseFixed: 2,
            drugDefault: JSON.stringify({
              mg: 5,
              ml: 1,
            }),
            drugFieldWidth: 0.35,
            drugOptions: [
              {
                label: '1mg/1mL',
                value: JSON.stringify({
                  mg: 1,
                  ml: 1,
                }),
              },
              {
                label: '5mg/1mL',
                value: JSON.stringify({
                  mg: 5,
                  ml: 1,
                }),
              },
            ],
            multiplyDoseMax: 5,
            observations: [
              'Diluir em 3-4mL de SF 0,9% estéril com fluxo de O2 de 6 a 8L/min (preferir nebulizar com fluxo de O2, ao invés de ar comprimido).',
            ],
            suggestions: [
              'Nebulização com 2,5mg (ou 0,15gm/Kg/dose) a cada 20 min na 1ª hora. Avaliar a necessidade de doses adicionais conforme a resposta do paciente.',
            ],
            templateThree: true,
            title: 'Salbutamol',
          },
        },
        {
          title: 'SALBUTAMOL SPRAY',
          content: {
            applicationUnitMeasure: 'de 20/20min na 1ª hora',
            drugDefault: JSON.stringify({
              mg: 100,
              ml: 1,
            }),
            drugFieldWidth: 0.44,
            drugOptions: [
              {
                label: '100mcg/1 puff',
                value: JSON.stringify({
                  mg: 100,
                  ml: 1,
                }),
              },
            ],
            observations: [
              'Se os sintomas persistirem ou retornarem, administrar doses adicionais de 2 a 3 puffs por hora.',
              'Se os sintomas melhorarem e o paciente receber alta, manter uso sob demanda ou 2 puffs a cada 4 a 6 horas por , se necessário).',
            ],
            suggestions: [
              'Aplicar 2 (leves) a 6 (graves) puffs a cada 20 minutos durante a primeira hora, depois reavaliar a gravidade (na prática normalmente se considera 1 puff para cada 3Kg, sendo no máximo 1- puffs).',
            ],
            templateFour: true,
            templateFourCalc2: true,
            title: 'Salbutamol',
          },
        },
        {
          title: 'IPRATRÓPIO NEB.',
          content: {
            applicationUnitMeasure: 'de 20/20min na 1ª hora',
            conditions: [
              '< 6 anos: 8 a 20 gotas',
              '- 6-12 anos: 20 gotas',
              '> 12 anos: 40 gotas',
              'Nebulizar com 3-4 mL SF0,9% a cada 20 min na 1ª hora',
            ],
            doseDefault: 0.15,
            drugDefault: JSON.stringify({
              mg: 0.25,
              ml: 1,
              drops: 20,
            }),
            drugFieldWidth: 0.58,
            drugOptions: [
              {
                label: '0,25mg/1mL/20 gotas',
                value: JSON.stringify({
                  mg: 0.25,
                  ml: 1,
                  drops: 20,
                }),
              },
            ],
            observations: ['Máx de até 3x na 1° hora'],
            templateFive: true,
            title: 'Ipratrópio',
          },
        },
        {
          title: 'IPRATRÓPIO SPRAY',
          content: {
            applicationUnitMeasure: 'de 20/20min na 1ª hora',
            conditions: [
              '< 6 anos: 1 a 2 puffs, a cada 20min na 1ª hora',
              '6-12 anos: 2 a 4 puffs, a cada 20min na 1ª hora',
              '⁠>= 12 anos: 4 a 8 puffs, a cada 20 min na 1ª hora',
            ],
            drugDefault: JSON.stringify({
              mg: 20,
              ml: 1,
            }),
            drugFieldWidth: 0.44,
            drugOptions: [
              {
                label: '20mcg/1 puff',
                value: JSON.stringify({
                  mg: 20,
                  ml: 1,
                }),
              },
            ],
            observations: ['Máx de até 3x na 1° hora'],
            templateFour: true,
            title: 'Ipratrópio',
          },
        },
        {
          title: 'FENOTEROL + IPRATRÓPIO SPRAY',
          content: {
            applicationUnitMeasure: 'de 20/20min na 1ª hora',
            conditions: [
              '< 12 anos: 1-2 puffs de 20/20 min na 1ª hora',
              '>= 12 anos: 2 puffs 20/20 min na 1ª hora',
            ],
            drugDefault: '1',
            drugFieldWidth: 0.58,
            drugNameBig: true,
            drugOptions: [
              {
                label: '50+20mcg/puff',
                value: '1',
              },
            ],
            observations: [
              'Máx até 3x na 1° hora',
              'Indicado para maiores de 6 anos (bula)',
            ],
            templateFour: true,
            title: 'Fenoterol + Ipratrópio',
          },
        },
        {
          title: 'SULF. MAGNÉSIO',
          content: {
            applicationText: 'Diluir',
            applicationUnitMeasure: 'mL + 100mL SF 0,9% IV lento (20-60 min)',
            diluteDefault: 1,
            diluteText: 'Reconst.',
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
              {
                label: 'mL',
                value: '2',
              },
            ],
            doseDefault: 40,
            drugDefault: JSON.stringify({
              mg: 1000,
              ml: 10,
            }),
            drugNameBig: true,
            drugOptions: [
              {
                label: '1000mg/10mL (10%)',
                value: JSON.stringify({
                  mg: 1000,
                  ml: 10,
                }),
              },
              {
                label: '5000mg/10mL (50%)',
                value: JSON.stringify({
                  mg: 5000,
                  ml: 10,
                }),
              },
            ],
            multiplyDoseMax: 2000,
            salineDefault: 5,
            salineLabel: 'SF 0,9%',
            suggestions: [
              'Dose única de 40-50 mg/kg (máx 2g) via infusão IV lenta (20-60 min)',
              'Reconstituir 1 ampola em 5mL SF 0,9% e aspirar a dose recomendada, e diluir em 100mL SF 0,9% IV lento (20-60 min)',
            ],
            templateOne: true,
            title: 'Sulf. Magnésio',
          },
        },
      ],
    },
    {
      title: 'Corticoides',
      topics: [
        {
          title: 'PREDNISONA COMP.',
          content: {
            applicationUnitMeasure: 'comp VO por dia',
            doseDefault: 0.5,
            doseUnitMeasure: 'mg/Kg/dia',
            drugDefault: JSON.stringify({
              mg: 5,
            }),
            drugFieldWidth: 0.3,
            drugOptions: [
              {
                label: '5mg',
                value: JSON.stringify({
                  mg: 5,
                }),
              },
              {
                label: '20mg',
                value: JSON.stringify({
                  mg: 20,
                }),
              },
            ],
            observations: [
              'Preferir doses únicas pela manhã para reduzir o risco de insônia induzida  por corticoides',
            ],
            suggestions: ['0,5 a 2mg/Kg/dia'],
            templateSix: true,
            title: 'Prednisona',
          },
        },
        {
          title: 'PREDNISOLONA SOL. ORAL',
          content: {
            applicationText: 'Dar',
            applicationUnitMeasure: 'mL VO de 24/24 horas',
            doseDefault: 0.5,
            doseUnitMeasure: 'mg/Kg/dia',
            drugDefault: JSON.stringify({
              mg: 3,
              ml: 1,
            }),
            drugFieldWidth: 0.35,
            drugOptions: [
              {
                label: '1mg/1mL',
                value: JSON.stringify({
                  mg: 1,
                  ml: 1,
                }),
              },
              {
                label: '3mg/1mL',
                value: JSON.stringify({
                  mg: 3,
                  ml: 1,
                }),
              },
            ],
            observations: [
              'Preferir doses únicas pela manhã para reduzir o risco de insônia induzida  por corticoides',
            ],
            suggestions: ['0,5 a 2mg/Kg/dia'],
            templateSix: true,
            title: 'Prednisolona',
            useDrugMl: true,
          },
        },
        {
          title: 'DEXAMETASONA SOL. ORAL',
          content: {
            applicationText: 'Dar',
            applicationUnitMeasure: 'mL VO de 24/24 horas',
            doseDefault: 0.07,
            doseFixed: 2,
            doseUnitMeasure: 'mg/Kg/dia',
            drugDefault: JSON.stringify({
              mg: 0.5,
              ml: 5,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '0,5mg/5mL',
                value: JSON.stringify({
                  mg: 0.5,
                  ml: 5,
                }),
              },
            ],
            observations: [
              'Preferir doses únicas pela manhã para reduzir o risco de insônia induzida  por corticoides',
            ],
            suggestions: ['0,07 a 0,3mg/Kg/dia'],
            templateSix: true,
            title: 'Dexametasona',
            useDrugMl: true,
          },
        },
        {
          title: 'METILPREDNISOLONA PÓ INJ.',
          content: {
            applicationUnitMeasure: 'mL IV bolus',
            diluteDefault: 1,
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
            ],
            doseDefault: 0.5,
            drugDefault: JSON.stringify({
              mg: 500,
            }),
            drugFieldWidth: 0.35,
            drugOptions: [
              {
                label: '40mg',
                value: JSON.stringify({
                  mg: 40,
                }),
              },
              {
                label: '125mg',
                value: JSON.stringify({
                  mg: 125,
                }),
              },
              {
                label: '500mg',
                value: JSON.stringify({
                  mg: 500,
                }),
              },
              {
                label: '1000mg',
                value: JSON.stringify({
                  mg: 1000,
                }),
              },
            ],
            observations: [
              '1mg/Kg de prednisolona equivale a 0,8mg/Kg de metilprednisolona',
            ],
            salineDefault: 25,
            salineLabel: 'SF 0,9%',
            suggestions: ['500mg + 25mL SF 0,9%', '0,5 a 1mg/Kg/dose'],
            templateOne: true,
            templateOneCalc2: true,
            title: 'Metilprednisolona',
          },
        },
        {
          title: 'HIDROCORTISONA PÓ INJ.',
          content: {
            applicationUnitMeasure: 'mL IV ou IM',
            diluteDefault: 1,
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
            ],
            doseDefault: 2.5,
            drugDefault: JSON.stringify({
              mg: 500,
            }),
            drugFieldWidth: 0.3,
            drugOptions: [
              {
                label: '100mg',
                value: JSON.stringify({
                  mg: 100,
                }),
              },
              {
                label: '500mg',
                value: JSON.stringify({
                  mg: 500,
                }),
              },
            ],
            observations: [
              '1mg/Kg de prednisolona equivale a 4mg/Kg de hidrocortisona',
            ],
            salineDefault: 5,
            salineLabel: 'SF 0,9%',
            suggestions: ['500mg + 5mL SF 0,9%', '2,5 a 5mg/Kg/dose'],
            templateOne: true,
            templateOneCalc2: true,
            title: 'Hidrocortisona',
          },
        },
        {
          title: 'DEXAMETASONA SOL. INJ.',
          content: {
            applicationUnitMeasure: 'mL IV ou IM',
            diluteDefault: 1,
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
              {
                label: 'mL',
                value: '2',
              },
            ],
            doseDefault: 0.09,
            doseFixed: 2,
            drugDefault: JSON.stringify({
              mg: 10,
              ml: 2.5,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '10mg/2,5mL',
                value: JSON.stringify({
                  mg: 10,
                  ml: 2.5,
                }),
              },
            ],
            observations: [
              '1mg/Kg de prednisolona equivale a 0,1mg/Kg de dexametasona',
            ],
            salineDefault: 0,
            salineLabel: 'SF 0,9%',
            suggestions: ['Não diluir', '0,09 a 0,19mg/Kg/dose'],
            templateOne: true,
            title: 'Dexametasona',
          },
        },
      ],
    },
    {
      title: 'Anafilaxia',
      topics: [
        {
          title: 'EPINEFRINA',
          content: {
            applicationUnitMeasure: 'mL IM',
            applicationUnitMeasure2: 'repetir até 2x a cada 5-15 min S/N',
            drugDefault: JSON.stringify({
              mg: 1,
              ml: 1,
            }),
            drugFieldWidth: 0.35,
            drugOptions: [
              {
                label: '1mg/1mL',
                value: JSON.stringify({
                  mg: 1,
                  ml: 1,
                }),
              },
            ],
            observations: [
              'Adultos/adolescentes: 0,5mg IM',
              '_',
              'Crianças: 0,01mg/Kg IM (máx 0,3mg)',
              '_',
              'Repetir dose em até 2 vezes, em intervalos de 5 a 15min (monitorar toxicidade pela frequência cardíaca)',
              '_',
              'Se refratário: Epinefrina 1mg/1mL Diluir 1 amp + 100mL Sf 0,9%, IV (acesso central) em BIC Dose: 0,1mcg/Kg/min',
              'Ex. 10Kg: Vazão de 6mL/h',
              'Ex. 15Kg: Vazão de 9mL/h',
              'Ex. 20Kg: Vazão de 12mL/h',
            ],
            templateSeven: true,
            title: 'Epinefrina',
          },
        },
        {
          title: 'METILPREDNISOLONA PÓ INJ.',
          content: {
            applicationUnitMeasure: 'mL IV bolus',
            diluteDefault: 1,
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
            ],
            doseDefault: 0.5,
            drugDefault: JSON.stringify({
              mg: 500,
            }),
            drugFieldWidth: 0.35,
            drugOptions: [
              {
                label: '40mg',
                value: JSON.stringify({
                  mg: 40,
                }),
              },
              {
                label: '125mg',
                value: JSON.stringify({
                  mg: 125,
                }),
              },
              {
                label: '500mg',
                value: JSON.stringify({
                  mg: 500,
                }),
              },
              {
                label: '1000mg',
                value: JSON.stringify({
                  mg: 1000,
                }),
              },
            ],
            observations: [
              '1mg/Kg de prednisolona equivale a 0,8mg/Kg de metilprednisolona',
            ],
            salineDefault: 25,
            salineLabel: 'SF 0,9%',
            suggestions: ['500mg + 25mL SF 0,9%', '0,5 a 1mg/Kg/dose'],
            templateOne: true,
            templateOneCalc2: true,
            title: 'Metilprednisolona',
          },
        },
        {
          title: 'HIDROCORTISONA PÓ INJ.',
          content: {
            applicationUnitMeasure: 'mL IV ou IM',
            diluteDefault: 1,
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
            ],
            doseDefault: 2.5,
            drugDefault: JSON.stringify({
              mg: 500,
            }),
            drugFieldWidth: 0.3,
            drugOptions: [
              {
                label: '100mg',
                value: JSON.stringify({
                  mg: 100,
                }),
              },
              {
                label: '500mg',
                value: JSON.stringify({
                  mg: 500,
                }),
              },
            ],
            observations: [
              '1mg/Kg de prednisolona equivale a 4mg/Kg de hidrocortisona',
            ],
            salineDefault: 5,
            salineLabel: 'SF 0,9%',
            suggestions: ['500mg + 5mL SF 0,9%', '2,5 a 5mg/Kg/dose'],
            templateOne: true,
            templateOneCalc2: true,
            title: 'Hidrocortisona',
          },
        },
        {
          title: 'DEXAMETASONA SOL. INJ.',
          content: {
            applicationUnitMeasure: 'mL IV ou IM',
            diluteDefault: 1,
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
              {
                label: 'mL',
                value: '2',
              },
            ],
            doseDefault: 0.09,
            doseFixed: 2,
            drugDefault: JSON.stringify({
              mg: 10,
              ml: 2.5,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '10mg/2,5mL',
                value: JSON.stringify({
                  mg: 10,
                  ml: 2.5,
                }),
              },
            ],
            observations: [
              '1mg/Kg de prednisolona equivale a 0,1mg/Kg de dexametasona',
            ],
            salineDefault: 0,
            salineLabel: 'SF 0,9%',
            suggestions: ['Não diluir', '0,09 a 0,19mg/Kg/dose'],
            templateOne: true,
            title: 'Dexametasona',
          },
        },
      ],
    },
    {
      title: 'Anticonvulsivantes',
      topics: [
        {
          title: 'DIAZEPAM',
          content: {
            applicationUnitMeasure: 'mL IV bolus',
            applicationUnitSubtitle:
              'Se a crise se mantém após 3-10 min, repetir a dose uma vez.',
            diluteDefault: 1,
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
              {
                label: 'mL',
                value: '2',
              },
            ],
            doseDefault: 0.2,
            drugDefault: JSON.stringify({
              mg: 10,
              ml: 2,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '10mg/2mL',
                value: JSON.stringify({
                  mg: 10,
                  ml: 2,
                }),
              },
            ],
            multiplyDoseMax: 10,
            salineDefault: 0,
            salineLabel: 'SF 0,9%',
            suggestions: [
              'Dose: 0,2 a 0,5mg/Kg/dose (máx 10mg)',
              'Se a crise se mantém após 3-10 min, repetir a dose uma vez.',
              'Administrar apenas por via IV (não administrar IM)',
            ],
            templateOne: true,
            title: 'Diazepam',
          },
        },
        {
          title: 'MIDAZOLAM',
          content: {
            applicationUnitMeasure: 'mL IV ou IM',
            applicationUnitSubtitle:
              'Se a crise se mantém após 3-10 min, repetir a dose uma vez (para via IV).',
            diluteDefault: 1,
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
              {
                label: 'mL',
                value: '2',
              },
            ],
            doseDefault: 0.1,
            doseFixed: 2,
            drugDefault: JSON.stringify({
              mg: 15,
              ml: 3,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '5mg/5mL',
                value: JSON.stringify({
                  mg: 5,
                  ml: 5,
                }),
              },
              {
                label: '15mg/3mL',
                value: JSON.stringify({
                  mg: 15,
                  ml: 3,
                }),
              },
              {
                label: '50mg/10mL',
                value: JSON.stringify({
                  mg: 50,
                  ml: 10,
                }),
              },
            ],
            multiplyDoseMax: 10,
            salineDefault: 0,
            salineLabel: 'SF 0,9%',
            suggestions: [
              'Dose: 0,1 a 0,2mg/Kg/dose (máx 10mg)',
              'Pode-se administrar IV ou IM',
            ],
            templateOne: true,
            title: 'Midazolam',
          },
        },
        {
          title: 'FENITOÍNA',
          content: {
            applicationText: 'Diluir',
            applicationUnitMeasure: 'mL + 250mL SF 0,9% IV lento',
            diluteDefault: 1,
            diluteText: 'Reconst.',
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
              {
                label: 'mL',
                value: '2',
              },
            ],
            doseDefault: 20,
            drugDefault: JSON.stringify({
              mg: 250,
              ml: 5,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '250mg/5mL',
                value: JSON.stringify({
                  mg: 250,
                  ml: 5,
                }),
              },
            ],
            multiplyDoseMax: 1000,
            salineDefault: 5,
            salineLabel: 'SF 0,9%',
            showSpeed: true,
            speedCalc2: true,
            speedText: 'Infundir em no mínimo',
            speedUnitMeasure: 'min',
            suggestions: [
              'Dose de ataque: 20 mg/Kg (máx 1.00 mg).',
              '_- Reconstituir 1 ampola em 5 mL SF0,9%, e diluir o volume recomendado em 250ml SF 0,9% (não diluir em SG 5%), e infundir IV lento.',
              '_- Velocidade de infusão máxima: 1 mg/Kg/min (máximo de 50 mg/min).',
              '_- Monitorização cardíaca contínua devido ao risco de prolongamento do intervalo QT com fenitoína.',
              'Dose de manutenção: 4 a 8 mg/Kg/dia.',
            ],
            templateOne: true,
            title: 'Fenitoína',
          },
        },
        {
          title: 'FENOBARBITAL',
          content: {
            applicationText: 'Diluir',
            applicationUnitMeasure: 'mL + 250mL SF 0,9% IV lento',
            diluteDefault: 1,
            diluteText: 'Reconst.',
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
              {
                label: 'mL',
                value: '2',
              },
            ],
            doseDefault: 20,
            drugDefault: JSON.stringify({
              mg: 200,
              ml: 2,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '200mg/1mL',
                value: JSON.stringify({
                  mg: 200,
                  ml: 1,
                }),
              },
              {
                label: '200mg/2mL',
                value: JSON.stringify({
                  mg: 200,
                  ml: 2,
                }),
              },
            ],
            multiplyDoseMax: 1000,
            salineDefault: 5,
            salineLabel: 'SF 0,9%',
            showSpeed: true,
            speedCalc2: true,
            speedText: 'Infundir em no mínimo',
            speedUnitMeasure: 'min',
            suggestions: [
              'Dose de ataque: 20 mg/Kg (máx 1.00 mg).',
              '_- Reconstituir 1 ampola em 5 mL SF0,9%, e diluir o volume recomendado em 250ml SF0,9%, e infundir IV lento.',
              '_- Velocidade de infusão máxima: 1 mg/Kg/min (máximo de 50 mg/min).',
              '_- Monitorização cardíaca contínua devido ao risco de prolongamento do intervalo QT com fenitoína.',
              'Dose de manutenção: 3 a 5 mg/Kg/dia.',
            ],
            templateOne: true,
            title: 'Fenobarbital',
          },
        },
      ],
    },
    {
      title: 'Taquiarritmias',
      topics: [
        {
          title: 'ADENOSINA',
          content: {
            applicationUnitMeasure: 'mL IV bolus',
            diluteDefault: 1,
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
              {
                label: 'mL',
                value: '2',
              },
            ],
            doseDefault: 0.1,
            doseFixed: 2,
            doseTwoCalc2: true,
            doseTwoDefault: 0.2,
            drugDefault: JSON.stringify({
              mg: 6,
              ml: 2,
            }),
            drugFieldWidth: 0.35,
            drugOptions: [
              {
                label: '6mg/2mL',
                value: JSON.stringify({
                  mg: 6,
                  ml: 2,
                }),
              },
            ],
            hideDoseTwoConditionText: true,
            multiplyDoseMax: 6,
            multiplyDoseTwoMax: 12,
            observations: [
              'Avisar previamente o paciente do provável efeito/sensação incômoda da adenosina.',
              'Manobras vagais, como a manobra de Valsalva, podem ser tentadas antes do uso de adenosina.',
            ],
            salineDefault: 4,
            salineLabel: 'SF 0,9%',
            showDoseTwo: true,
            suggestions: [
              '1ª dose: 0,1 mg/Kg (máx 6 mg) IV bolus + flush 10ml de SF 0,9% + eleva o membro.',
              '2ª dose: 0,2 mg/Kg (máx 6 mg) IV bolus + flush 10ml de SF 0,9% + eleva o membro.',
              'Administração: Injeção intravenosa rápida, de preferência em veia de grande calibre, com monitoração contínua do ECG (D2 longo).',
            ],
            templateOne: true,
            title: 'Adenosina',
          },
        },
        {
          title: 'AMIODARONA',
          content: {
            applicationText: 'Diluir',
            applicationUnitMeasure: 'mL + 250mL SG 5% IV',
            diluteDefault: 1,
            diluteText: 'Reconst.',
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
              {
                label: 'mL',
                value: '2',
              },
            ],
            doseDefault: 5,
            drugDefault: JSON.stringify({
              mg: 150,
              ml: 3,
            }),
            drugFieldWidth: 0.4,
            drugOptions: [
              {
                label: '150mg/3mL',
                value: JSON.stringify({
                  mg: 150,
                  ml: 3,
                }),
              },
            ],
            multiplyDoseMax: 150,
            salineDefault: 5,
            salineLabel: 'SG 5%',
            suggestions: [
              'Dose: 5mg/Kg (ataque), podendo repetir mais duas vezes (total: 15m/Kg/dia)',
              'Diluir somente em SG 5% (100-250mL). Não diluir em SF 0,9%.',
            ],
            templateOne: true,
            title: 'Amiodarona',
          },
        },
        {
          title: 'CARDIOVERSÃO ELÉTRICA',
          content: {
            chargeDefault: 0.5,
            chargeTwoDefault: 2,
            description:
              'Afastar a equipe e o fluxo de O2 + analgesia e sedação',
            hideChargeThree: true,
            templateTwo: true,
            title: 'Cardioversão elétrica',
          },
        },
      ],
    },
    {
      title: 'Bradiarritmias',
      topics: [
        {
          title: 'ATROPINA',
          content: {
            applicationUnitMeasure:
              'mL IV bolus. Podendo repetir 1 vez após 5 min s/n',
            diluteDefault: 1,
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
              {
                label: 'mL',
                value: '2',
              },
            ],
            doseDefault: 0.02,
            doseFixed: 2,
            drugDefault: JSON.stringify({
              mg: 0.25,
              ml: 1,
            }),
            drugFieldWidth: 0.44,
            drugOptions: [
              {
                label: '0,25mg/1mL',
                value: JSON.stringify({
                  mg: 0.25,
                  ml: 1,
                }),
              },
              {
                label: '0,5mg/1mL',
                value: JSON.stringify({
                  mg: 0.5,
                  ml: 1,
                }),
              },
            ],
            multiplyDoseMax: 0.5,
            salineDefault: 0,
            salineLabel: 'SF 0,9%',
            suggestions: [
              'Crianças: a dose é de 0,02mg/Kg/dose (máx 0,5mg/dose), podendo repetir 1x após 5min',
              '_',
              'Adultos/adolescentes: a dose é de 1mg IV bolus, a cada 3 a 5min (máx 3mg)',
            ],
            templateOne: true,
            title: 'Atropina',
          },
        },
        {
          title: 'EPINEFRINA',
          content: {
            applicationUnitMeasure: 'mL IV bolus',
            diluteDefault: 1,
            diluteUnitMeasureDefault: '1',
            diluteUnitMeasureOptions: [
              {
                label: 'ampola',
                value: '1',
              },
              {
                label: 'mL',
                value: '2',
              },
            ],
            doseDefault: 0.01,
            doseFixed: 2,
            doseTwoDefault: 0.1,
            drugDefault: JSON.stringify({
              mg: 1,
              ml: 1,
            }),
            drugFieldWidth: 0.35,
            drugOptions: [
              {
                label: '1mg/1mL',
                value: JSON.stringify({
                  mg: 1,
                  ml: 1,
                }),
              },
            ],
            multiplyDoseMax: 1,
            multiplyDoseTwoMax: 2.5,
            observations: [
              'Intravenoso (IV): Após cada dose, aplicar flush de 10mL de SF 0,9% e elevar membro',
            ],
            salineDefault: 9,
            salineLabel: 'SF 0,9%',
            showDoseTwo: true,
            suggestions: [
              'Intravenoso (IV): Diluir 1 amp + 9mL SF 0,9% e infundir uma dose de 0,01mg/Kg (máx: 1mg)',
              'Intraósseo (IO): Dose de 0,01mg/Kg (máx: 1mg)',
              'Endotraqueal: Dose de 0,1mg/kg (máx 2,5mg) (não diluir)',
            ],
            templateOne: true,
            title: 'Epinefrina',
          },
        },
      ],
    },
  ],
};

const formSchema = z.object({
  weight: z.string().min(1, {
    message: errorsValidation.required,
  }),
  ageMonth: z.string().min(1, {
    message: errorsValidation.required,
  }),
  ageYear: z.string().min(1, {
    message: errorsValidation.required,
  }),
});

function PediatricEmergencyDose() {
  const { user } = useAuth();
  const { onScrollTo } = useScroll();
  const [topics, setTopics] = useState<Topics[]>([]);
  const { ageMonth, ageYear, setAgeMonth, setAgeYear, setWeight, weight } =
    useCalculator();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    defaultValues: {
      weight: weight.toFixed(1),
      ageMonth: String(ageMonth),
      ageYear: String(ageYear),
    },
  });

  const handleSetTopics = (topics: Topics[]) => {
    onScrollTo();
    setTopics(topics);
  };

  const handleClearTopics = () => {
    setTopics([]);
    onScrollTo();
  };

  const handleChangeWeight = useCallback(
    (value: string): void => {
      const valueParsed = Number(value.replace(',', '.'));
      setWeight(valueParsed);
    },
    [setWeight],
  );

  const handleChangeAgeYear = useCallback(
    (value: string): void => {
      const valueParsed = Number(value.replace(',', '.'));
      setAgeYear(valueParsed);
    },
    [setAgeYear],
  );

  const handleChangeAgeMonth = useCallback(
    (value: string): void => {
      const valueParsed = Number(value.replace(',', '.'));
      setAgeMonth(valueParsed);
    },
    [setAgeMonth],
  );

  function onSubmit(): void {
    //
  }

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'weight' && value.weight) {
        handleChangeWeight(value.weight);
      }
      if (name === 'ageYear' && value.ageYear) {
        handleChangeAgeYear(value.ageYear);
      }
      if (name === 'ageMonth' && value.ageMonth) {
        handleChangeAgeMonth(value.ageMonth);
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  useEffect(() => {
    setWeight(0);
    form.setValue('weight', '0');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user?.accessGranted) {
    return <Navigate replace to="/pagamento" />;
  }

  return (
    <div>
      <PageTitle hasBackButton title="Dose emergência pediatria" />
      <Form {...form}>
        <form
          className="bg-background sticky top-0 z-[9] py-4"
          onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex justify-center gap-2">
            <FormField
              control={form.control}
              name="weight"
              render={({ field: { ref, ...rest } }) => (
                <FormItem className="flex flex-col items-center">
                  <FormLabel>Peso</FormLabel>
                  <FormControl>
                    <NumberFormatBase
                      className="[&_input]:w-26 [&_input]:text-center"
                      customInput={Input}
                      format={(value) => formatterDecimal(value, 1)}
                      getCaretBoundary={(formattedValue) => {
                        const boundaryAry = Array.from({
                          length: formattedValue.length + 1,
                        }).map(() => true);
                        boundaryAry[0] = false;
                        boundaryAry[1] = false;
                        boundaryAry[2] = false;
                        return boundaryAry;
                      }}
                      getInputRef={ref}
                      {...rest}
                    />
                  </FormControl>
                  <FormDescription>Kg</FormDescription>
                </FormItem>
              )}
            />
            <div className="flex flex-col items-center justify-end gap-2">
              <Label>Idade</Label>
              <div className="flex flex-row gap-2">
                <FormField
                  control={form.control}
                  name="ageYear"
                  render={({ field: { ref, ...rest } }) => (
                    <FormItem className="flex flex-col items-center">
                      <FormControl>
                        <NumericFormat
                          className="[&_input]:w-20 [&_input]:text-center"
                          customInput={Input}
                          getInputRef={ref}
                          maxLength={2}
                          {...rest}
                        />
                      </FormControl>
                      <FormDescription>Anos</FormDescription>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="ageMonth"
                  render={({ field: { ref, ...rest } }) => (
                    <FormItem className="flex flex-col items-center">
                      <FormControl>
                        <NumericFormat
                          className="[&_input]:w-20 [&_input]:text-center"
                          customInput={Input}
                          getInputRef={ref}
                          maxLength={2}
                          {...rest}
                        />
                      </FormControl>
                      <FormDescription>Meses</FormDescription>
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>
          {!!topics.length && (
            <div className="mx-auto max-w-3xl">
              <button
                className="text-foreground mt-4 flex cursor-pointer flex-row items-center text-lg font-medium"
                onClick={handleClearTopics}
                title="Voltar"
                type="button">
                <ChevronLeft className="size-7" strokeWidth={2} />
                <h1>Voltar</h1>
              </button>
            </div>
          )}
        </form>
      </Form>
      <div
        className={cn(
          'mx-auto max-w-3xl transition-all duration-500',
          topics.length ? 'animate-out fade-out hidden' : 'animate-in fade-in',
        )}>
        {pediatricEmergencyDose.sections.map((item) => (
          <button
            className="ring-ring/10 dark:ring-ring/20 dark:outline-ring/40 outline-ring/50 flex w-full flex-1 cursor-pointer items-start justify-between gap-4 rounded-md py-4 text-left text-lg font-medium uppercase transition-all hover:underline hover:opacity-70 focus-visible:outline-1 focus-visible:ring-4 disabled:pointer-events-none disabled:opacity-50"
            key={item.title}
            onClick={() => handleSetTopics(item.topics)}>
            {item.title}
            <ChevronRightIcon className="text-muted-foreground pointer-events-none size-4 shrink-0 translate-y-0.5 transition-transform duration-200" />
          </button>
        ))}
      </div>
      <div
        className={cn(
          'mx-auto max-w-3xl transition-all duration-500',
          topics.length ? 'animate-in fade-in' : 'animate-out fade-out hidden',
        )}>
        {topics.map((item) => (
          <Content
            applicationText={item.content.applicationText}
            applicationUnitMeasure={item.content.applicationUnitMeasure}
            applicationUnitMeasure2={item.content.applicationUnitMeasure2}
            applicationUnitSubtitle={item.content.applicationUnitSubtitle}
            chargeDefault={item.content.chargeDefault}
            chargeTwoDefault={item.content.chargeTwoDefault}
            conditions={item.content.conditions}
            description={item.content.description}
            diluteDefault={item.content.diluteDefault}
            diluteText={item.content.diluteText}
            diluteUnitMeasureDefault={item.content.diluteUnitMeasureDefault}
            diluteUnitMeasureOptions={item.content.diluteUnitMeasureOptions}
            doseDefault={item.content.doseDefault}
            doseFixed={item.content.doseFixed}
            doseTwoCalc2={item.content.doseTwoCalc2}
            doseTwoDefault={item.content.doseTwoDefault}
            doseUnitMeasure={item.content.doseUnitMeasure}
            drugDefault={item.content.drugDefault}
            drugFieldWidth={item.content.drugFieldWidth}
            drugNameBig={item.content.drugNameBig}
            drugOptions={item.content.drugOptions}
            hideChargeThree={item.content.hideChargeThree}
            hideDoseTwoConditionText={item.content.hideDoseTwoConditionText}
            key={item.title}
            max={item.content.max}
            multiplyDoseMax={item.content.multiplyDoseMax}
            multiplyDoseTwoMax={item.content.multiplyDoseTwoMax}
            nebMax={item.content.nebMax}
            observations={item.content.observations}
            salineDefault={item.content.salineDefault}
            salineLabel={item.content.salineLabel}
            showBIC={item.content.showBIC}
            showDoseTwo={item.content.showDoseTwo}
            showSpeed={item.content.showSpeed}
            speedCalc2={item.content.speedCalc2}
            speedText={item.content.speedText}
            speedUnitMeasure={item.content.speedUnitMeasure}
            suggestions={item.content.suggestions}
            templateEight={item.content.templateEight}
            templateFive={item.content.templateFive}
            templateFour={item.content.templateFour}
            templateFourCalc2={item.content.templateFourCalc2}
            templateOne={item.content.templateOne}
            templateOneCalc2={item.content.templateOneCalc2}
            templateOneCalc3={item.content.templateOneCalc3}
            templateSeven={item.content.templateSeven}
            templateSevenCalc10={item.content.templateSevenCalc10}
            templateSevenCalc11={item.content.templateSevenCalc11}
            templateSevenCalc12={item.content.templateSevenCalc12}
            templateSevenCalc2={item.content.templateSevenCalc2}
            templateSevenCalc3={item.content.templateSevenCalc3}
            templateSevenCalc4={item.content.templateSevenCalc4}
            templateSevenCalc5={item.content.templateSevenCalc5}
            templateSevenCalc6={item.content.templateSevenCalc6}
            templateSevenCalc7={item.content.templateSevenCalc7}
            templateSevenCalc8={item.content.templateSevenCalc8}
            templateSevenCalc9={item.content.templateSevenCalc9}
            templateSix={item.content.templateSix}
            templateSixCalc2={item.content.templateSixCalc2}
            templateSixCalcDrops={item.content.templateSixCalcDrops}
            templateThree={item.content.templateThree}
            templateTwo={item.content.templateTwo}
            title={item.title}
            useDrugMl={item.content.useDrugMl}
          />
        ))}
      </div>
    </div>
  );
}

export default PediatricEmergencyDose;
